import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, CaretLeft, CaretRight, MicrosoftExcelLogo, Calendar } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { shizaiApi } from './shizaiApi';

const NONE = '__none__';

function fmtMonth(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}
function fmtJaMonth(iso) {
  if (!iso || !/^\d{4}-\d{2}$/.test(iso)) return iso;
  const [y, m] = iso.split('-');
  return `${y}年${Number(m)}月`;
}
function shiftMonth(iso, delta) {
  const [y, m] = iso.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return fmtMonth(d);
}

/**
 * 経理データ出力 モーダル
 * User picks a format (経理データ / TECHSデータ互換), a month + optional dept,
 * sees a live preview (件数 / 金額) then downloads xlsx.
 */
export default function ExportMonthlyDialog({ open, onClose, departments = [] }) {
  const [format, setFormat] = useState('monthly'); // 'monthly' | 'techs'
  const [month, setMonth] = useState(() => fmtMonth(new Date()));
  const [deptId, setDeptId] = useState('');
  const [preview, setPreview] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!open) return;
    const params = { month };
    if (deptId) params.department_id = deptId;
    setPreview(null);
    const fetch = format === 'techs' ? shizaiApi.exportTechsPreview : shizaiApi.exportMonthlyPreview;
    fetch(params).then(setPreview).catch(() => setPreview({ error: true }));
  }, [open, month, deptId, format]);

  const download = async () => {
    setDownloading(true);
    try {
      const params = { month };
      if (deptId) params.department_id = deptId;
      // We need auth headers — fetch via axios then trigger blob download
      const url = format === 'techs'
        ? shizaiApi.exportTechsUrl(params)
        : shizaiApi.exportMonthlyUrl(params);
      const res = await axios.get(url, { responseType: 'blob' });
      const disposition = res.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename\*=UTF-8''([^;]+)/);
      const fallback = format === 'techs' ? `TECHSデータ_${month}.xlsx` : `経理データ_${month}.xlsx`;
      const fname = filenameMatch ? decodeURIComponent(filenameMatch[1]) : fallback;
      const blobUrl = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = blobUrl; a.download = fname; document.body.appendChild(a);
      a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success('ダウンロードしました');
    } catch (e) {
      toast.error('ダウンロードに失敗しました');
    } finally {
      setDownloading(false);
    }
  };

  const yen = (n) => new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', maximumFractionDigits: 0 }).format(n || 0);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="export-monthly-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MicrosoftExcelLogo size={22} weight="duotone" className="text-emerald-600" />
            経理データ出力
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Format selector — 経理データ (dark banner, tax rollup) vs TECHSデータ (legacy 22-col) */}
          <div>
            <Label className="text-xs font-semibold text-zinc-600">出力形式</Label>
            <div className="grid grid-cols-2 gap-2 mt-1.5" data-testid="export-format-toggle">
              <button
                type="button"
                onClick={() => setFormat('monthly')}
                data-testid="export-format-monthly"
                className={`px-3 py-2 rounded-md border text-xs font-semibold transition-colors ${
                  format === 'monthly'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-800 shadow-sm'
                    : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300'
                }`}
              >
                <div className="text-sm font-bold">経理データ</div>
                <div className="text-[10px] text-zinc-500 mt-0.5">明細 + 集計 (税込)</div>
              </button>
              <button
                type="button"
                onClick={() => setFormat('techs')}
                data-testid="export-format-techs"
                className={`px-3 py-2 rounded-md border text-xs font-semibold transition-colors ${
                  format === 'techs'
                    ? 'border-emerald-500 bg-emerald-50 text-emerald-800 shadow-sm'
                    : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300'
                }`}
              >
                <div className="text-sm font-bold">TECHSデータ</div>
                <div className="text-[10px] text-zinc-500 mt-0.5">レガシー22列互換 (税抜)</div>
              </button>
            </div>
          </div>

          {/* Month picker with prev/next */}
          <div>
            <Label className="text-xs font-semibold text-zinc-600 flex items-center gap-1.5">
              <Calendar size={13} weight="duotone" className="text-emerald-500" /> 対象月
            </Label>
            <div className="flex items-center gap-1 mt-1.5" data-testid="export-month-picker">
              <Button variant="outline" size="sm" onClick={() => setMonth((m) => shiftMonth(m, -1))} data-testid="export-month-prev" className="h-10 w-10 p-0">
                <CaretLeft size={16} weight="bold" />
              </Button>
              <div className="flex-1 text-center h-10 rounded-md border border-zinc-200 bg-zinc-50 flex items-center justify-center font-semibold text-zinc-800" data-testid="export-month-label">
                {fmtJaMonth(month)}
              </div>
              <Button variant="outline" size="sm" onClick={() => setMonth((m) => shiftMonth(m, 1))} data-testid="export-month-next" className="h-10 w-10 p-0">
                <CaretRight size={16} weight="bold" />
              </Button>
            </div>
            <p className="text-[11px] text-zinc-400 mt-1.5">検収日（受入日）が対象月に含まれる発注が集計されます。</p>
          </div>

          {/* Dept filter */}
          <div>
            <Label className="text-xs font-semibold text-zinc-600">部門で絞込</Label>
            <Select value={deptId || NONE} onValueChange={(v) => setDeptId(v === NONE ? '' : v)}>
              <SelectTrigger data-testid="export-dept-select" className="h-10 mt-1"><SelectValue placeholder="全部門" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>全部門</SelectItem>
                {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Preview card */}
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-2" data-testid="export-preview">
            {preview?.error ? (
              <p className="text-sm text-rose-600">プレビューの取得に失敗しました</p>
            ) : !preview ? (
              <p className="text-sm text-zinc-500">集計中...</p>
            ) : preview.line_count === 0 ? (
              <p className="text-sm text-zinc-500">この期間の検収データはありません</p>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <p className="text-[10px] font-semibold uppercase text-zinc-500">明細件数</p>
                  <p className="text-lg font-bold text-zinc-900 tabular-nums" data-testid="export-line-count">{preview.line_count}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase text-zinc-500">仕入先数</p>
                  <p className="text-lg font-bold text-zinc-900 tabular-nums">{preview.supplier_count}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase text-zinc-500">税込合計</p>
                  <p className="text-lg font-bold text-emerald-700 tabular-nums" data-testid="export-total">{yen(preview.incl_tax_total)}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>キャンセル</Button>
          <Button data-testid="export-download-btn" onClick={download}
            disabled={downloading || !preview || preview.error || preview.line_count === 0}
            className="bg-teal-700 hover:bg-teal-800 disabled:opacity-50">
            <Download size={16} weight="bold" className="mr-1.5" />
            {downloading
              ? 'ダウンロード中...'
              : (format === 'techs' ? 'TECHSデータ をダウンロード' : 'xlsx をダウンロード')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
